import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { GenericMessageBox } from './messages/GenericMessageBox'
import { MessageBox } from './messages/MessageBox'
import { logout } from '../../account/services/auth'
import { destroySession } from '../../account/services/session'
import { clear } from '../../app-reducer'
import FormGroup from '../../core/components/FormGroup'
import {
  getUserProfile,
  IUserProfile,
  IUserProfilePayload,
  updateUserProfile,
} from '../../core/services/menu'
import { useAppDispatch } from '../../store'
import { Header, PageTitle } from '@/components'
import { Card } from '@/components/ui/card'
import { Button, Input } from '@/components/ui'
import Loader from '@/common/components/Loader'

interface IUserValues {
  firstName: string
  lastName: string
  email: string
  password?: string | null
  password2?: string | null
}

export const MyProfilePage: React.FC = () => {
  const [user, setUser] = useState<IUserProfile>()
  const [userPayload, setUserPayload] = useState<IUserProfilePayload>()

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [showGenericMessageBox, setShowGenericMessageBox] =
    useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const response = await getUserProfile()
        if (response.status = 200) {
          setUser(response.data)
          setValue('firstName', response.data.firstName)
          setValue('lastName', response.data.lastName)
          setValue('email', response.data.email)
        }
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      reset()
    }
  }, [])

  const schema = yup.object().shape({
    firstName: yup.string().required('Field is required'),
    lastName: yup.string().required('Field is required'),
    email: yup.string().required('Field is required'),
    password: yup
      .string()
      .notRequired()
      .min(6)
      .nullable()
      .transform((value) => (!!value ? value : null)),
    password2: yup
      .string()
      .notRequired()
      .min(6)
      .nullable()
      .transform((value) => (!!value ? value : null)),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<IUserValues>({
    mode: 'onChange',
    resolver: yupResolver<IUserValues>(schema),
    defaultValues: {
      password: null,
      password2: null,
    },
  })

  const pass1 = watch('password')
  const pass2 = watch('password2')

  const submit = async (values: IUserValues) => {
    const payload: IUserProfilePayload = {
      id: user?.id || '',
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
    } as IUserProfilePayload
    if (values.password) {
      payload.password = values.password
      setShowGenericMessageBox(true)
      setUserPayload(payload)
    } else {
      try {
        setLoading(true)
        const { status, data } = await updateUserProfile(payload)
        if (status === 200) {
          setValue('firstName', data.firstName)
          setValue('lastName', data.lastName)
          setValue('email', data.email)
          setUser(data)
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    }
  }

  const handleConfirmSubmit = async () => {
    try {
      if (userPayload) {
        const resp = await updateUserProfile(userPayload)
        if (resp.status === 200) {
          const { status } = await logout()
          if (status === 200) {
            navigate('/account/login', {
              replace: true,
            })
            destroySession()
            dispatch(clear())
          }
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (user) {
      setValue('firstName', user.firstName)
      setValue('lastName', user.lastName)
      setValue('email', user.email)
      setValue('password', null, { shouldValidate: true })
      setValue('password2', null, { shouldValidate: true })
    }
  }

  const disabled =
    (pass1 !== null && !!pass1 && pass1.length < 6) ||
    (pass2 !== null && !!pass2 && pass2.length < 6) ||
    pass1 !== pass2

  return (
    <>
      {loading && <Loader />}
      <Header
        pageTitle="My Profile"
        actions={
          <div className="flex gap-2">
            <Button
              onClick={handleCancel}
              variant="secondary"
              data-dismiss="modal"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(submit)}
              variant="primary"
              form="settings-form"
              type="submit"
            >
              Save
            </Button>
          </div>
        }
      />
      <Card className="!bg-monochrome4-60 p-5 backdrop-blur-lg max-h-[calc(100vh-150px)] overflow-y-auto">
        <PageTitle title="My Profile" />

        <form
          id="settings-form"
          className="space-y-7 mt-4"
          onSubmit={handleSubmit(submit)}
        >
          <FormGroup label="First Name">
            <Input
              isInvalid={!!errors?.firstName?.message}
              {...register('firstName')}
              type="text"
              placeholder="Enter First Name"
            />
          </FormGroup>
          <FormGroup label="Last Name">
            <Input
              isInvalid={!!errors?.lastName?.message}
              {...register('lastName')}
              type="text"
              placeholder="Enter Last Name"
            />
          </FormGroup>
          <FormGroup label="Email">
            <Input
              isInvalid={!!errors?.email?.message}
              {...register('email')}
              type="text"
              disabled
            />
          </FormGroup>
          <FormGroup label="Password">
            <Input
              isInvalid={!!errors?.password?.message}
              {...register('password')}
              type="password"
              placeholder="Enter Password"
            />
          </FormGroup>
          <FormGroup label="Repeat Password">
            <Input
              isInvalid={!!errors?.password2?.message}
              {...register('password2')}
              type="password"
              placeholder="Enter Password Again"
            />
          </FormGroup>
        </form>
      </Card>
      <GenericMessageBox
        title="Changing Password"
        message="Are you sure you want to change the password? You will have to login again."
        hasCancelButton={true}
        hasSubmitButton={true}
        cancelButtonTitle="No"
        submitButtonTitle="Confirm"
        submitButtonAction={handleConfirmSubmit}
        setShowMessageBox={setShowGenericMessageBox}
        showMessageBox={showGenericMessageBox}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
